<template>
    <div class="ps-2">
        <div class="d-flex align-center text-header">
            <i class="mdi-email mdi v-icon me-2" style="font-size: 1.5rem; padding-bottom: 1px"></i>
            <span v-if="type === 'expert'">{{ t('toasts.invitations.expert_invite') }}</span>
            <span v-else-if="type === 'group'">{{ t('toasts.invitations.group_invite') }}</span>
            <span v-else-if="type === 'workspace'">
                {{ t('toasts.invitations.workspace_invite') }}
            </span>
        </div>
        <div class="my-2 text-subheader">
            <span
                v-if="type === 'expert'"
                v-html="
                    t('toasts.invitations.expert_invite_message', {
                        role: invite?.role_name,
                        expert: invite?.expert_name,
                    })
                "
            ></span>

            <span
                v-else-if="type === 'group'"
                v-html="
                    t('toasts.invitations.group_invite_message', { group: invite?.group?.name })
                "
            ></span>

            <span
                v-else-if="type === 'workspace'"
                v-html="
                    t('toasts.invitations.workspace_invite_message', {
                        workspace: invite?.workspace?.name,
                        email:
                            invite?.workspace?.name === 'Personal'
                                ? `(${truncateText(invite?.workspace?.created_user?.email, 20)})`
                                : '',
                    })
                "
            ></span>
        </div>
        <div class="d-flex align-end justify-end mt-4">
            <button
                class="accept-button d-flex align-center"
                :name="`button-accept-${type}-invitation`"
                @click="accept_invite"
            >
                <i class="mdi-check mdi v-icon"></i>
                Accept
            </button>
            <button
                class="decline-button d-flex align-center"
                :name="`button-decline-${type}-invitation`"
                @click="decline_invite"
            >
                <i class="mdi-delete mdi v-icon"></i>
                Decline
            </button>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useUserStore } from '@/stores/user';
import { useExpertStore } from '@/stores/expert';
import { mainAxios as axios } from '@/assets/js/axios_config';
import { toast } from 'vue3-toastify';
import i18n from '@/plugins/i18n.js';
const { t } = i18n.global;

const props = defineProps({
    closeToast: {
        type: Function,
        default: () => {},
    },
    toastProps: {
        type: Object,
        default: () => ({}),
    },
    translateFunction: {
        type: Function,
        default: (key) => key,
    },
});

const user = useUserStore();
const expertStore = useExpertStore();
const type = ref(null);
const invite = ref(null);

onMounted(() => {
    invite.value = props.toastProps.data.invite;
    type.value = props.toastProps.data.type;
});

const accept_expert_invite = async () => {
    await axios
        .post(`/permission/accept_invite/${invite.value.id}`)
        .then(async (response) => {
            await expertStore.loadData();
            expertStore.highlight_expert_as_new(response.data.expert_id);
        })
        .catch((error) => {
            if (error?.response?.data?.detail) {
                toast.error(error.response.data.detail);
            } else {
                toast.error(t('alerts.error.something_went_wrong'));
            }
        })
        .finally(() => {
            props.closeToast();
        });
};

const accept_group_invite = async () => {
    await axios
        .post(`/groups/accept_invite/${invite.value.id}`)
        .then(() => {
            toast.success('Group invite accepted.');
            user.load_groups(true);
        })
        .catch((error) => {
            if (error?.response?.data?.detail) {
                toast.error(error.response.data.detail);
            } else {
                toast.error(t('alerts.error.something_went_wrong'));
            }
        })
        .finally(() => {
            props.closeToast();
        });
};

const accept_workspace_invite = async () => {
    await axios
        .post(`/workspace/permissions/${invite.value.id}/accept`)
        .then(() => {
            toast.success(t('alerts.success.workspace_invite_accepted'));
        })
        .catch((error) => {
            if (error?.response?.data?.detail) {
                toast.error(error.response.data.detail);
            } else {
                toast.error(t('alerts.error.something_went_wrong'));
            }
        })
        .finally(() => {
            props.closeToast();
        });
};

const accept_invite = async () => {
    if (type.value === 'expert') {
        await accept_expert_invite();
    } else if (type.value === 'group') {
        await accept_group_invite();
    } else if (type.value === 'workspace') {
        await accept_workspace_invite();
    }
};

const decline_expert_invite = async () => {
    await axios.delete(`/permission/expert_invite/${invite.value.id}`);
    props.closeToast();
};

const decline_group_invite = async () => {
    await axios
        .post(`/groups/decline_invite/${invite.value.id}`)
        .then(() => {
            toast.success(t('alerts.success.group_invite_accespted'));
        })
        .catch((error) => {
            if (error.response.status === 400 && error.response.data.detail) {
                toast.error(t('alerts.error.not_invited_to_group'));
            } else if (error.response.status === 404 && error.response.data.detail) {
                toast.error(error.response.data.detail);
            } else {
                toast.error(t('alerts.error.something_went_wrong'));
            }
        })
        .finally(() => {
            props.closeToast();
        });
};

const decline_workspace_invite = async () => {
    await axios
        .delete(`/workspace/permissions/${invite.value.id}/decline`)
        .then(() => {
            toast.success(t('alerts.error.workspace_invite_declined'));
        })
        .catch((error) => {
            if (error.response.status === 400 && error.response.data.detail) {
                toast.error(t('alerts.error.not_invited_to_workspace'));
            } else if (error.response.status === 404 && error.response.data.detail) {
                toast.error(error.response.data.detail);
            } else {
                toast.error(t('alerts.error.something_went_wrong'));
            }
        })
        .finally(() => {
            props.closeToast();
        });
};

const decline_invite = async () => {
    if (type.value === 'expert') {
        await decline_expert_invite();
    } else if (type.value === 'group') {
        await decline_group_invite();
    } else if (type.value === 'workspace') {
        await decline_workspace_invite();
    }
};

const truncateText = (text, length) => {
    if (text.length > length) {
        return text.substring(0, length) + '...';
    }
    return text;
};
</script>

<style lang="scss" scoped>
.accept-button {
    background-color: black;
    border: 1px solid rgb(228, 228, 231);
    color: white;
    padding: 5px 10px;
    border-radius: 5px;
    margin-right: 10px;

    &:hover {
        opacity: 0.8;
    }
}

.decline-button {
    border: 1px solid rgb(228, 228, 231);
    color: black;
    padding: 5px 10px;
    border-radius: 5px;
    box-shadow:
        rgba(0, 0, 0, 0) 0px 0px 0px 0px,
        rgba(0, 0, 0, 0) 0px 0px 0px 0px,
        rgba(0, 0, 0, 0.05) 0px 1px 2px 0px;

    &:hover {
        background-color: rgb(228, 228, 231);
    }
}

.text-header {
    font-size: 1rem;
    font-weight: 600;
    color: black;
    line-height: 1;
}

.text-subheader {
    line-height: 1.5;
}
</style>
